import { useCallback, useEffect, useState } from 'react'
import apiClient from '../api/client'
import { useAuth } from '../auth/useAuth'
import EmptyState from '../components/EmptyState'
import ErrorAlert from '../components/ErrorAlert'
import LoadingSpinner from '../components/LoadingSpinner'

interface Template {
  id: string
  title: string
  content: string
  type: string
}

interface KnowledgeResponse {
  // La clave del listado la define `portal_api/util/dynamodb_helpers._KEY_MAP`:
  // para KNOWLEDGE es `knowledge`. Antes acá decía `items` y las plantillas
  // nunca cargaban — la pantalla mostraba el estado vacío sin ningún error.
  knowledge: Template[]
}

interface PreviewResponse {
  preview_text: string
  phone: string
  template_id: string
}

export default function Outreach() {
  const { canSupervise } = useAuth()
  const canUse = canSupervise

  const [templates, setTemplates] = useState<Template[]>([])
  const [loadingTemplates, setLoadingTemplates] = useState(true)
  const [templatesError, setTemplatesError] = useState<string | null>(null)

  const [selectedTemplate, setSelectedTemplate] = useState('')
  const [phone, setPhone] = useState('')
  const [preview, setPreview] = useState<PreviewResponse | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [previewing, setPreviewing] = useState(false)
  const [sending, setSending] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [successMsg, setSuccessMsg] = useState<string | null>(null)

  const fetchTemplates = useCallback(async () => {
    setLoadingTemplates(true)
    setTemplatesError(null)
    try {
      const response = await apiClient.get<KnowledgeResponse>('/knowledge?type=TEMPLATE')
      setTemplates(response.data.knowledge ?? [])
    } catch (err) {
      setTemplatesError(err instanceof Error ? err.message : 'Error al cargar plantillas')
    } finally {
      setLoadingTemplates(false)
    }
  }, [])

  useEffect(() => { if (canUse) void fetchTemplates() }, [canUse, fetchTemplates])

  const handlePreview = async () => {
    if (!selectedTemplate || !phone.trim()) return
    setPreviewing(true)
    setActionError(null)
    try {
      const response = await apiClient.post<PreviewResponse>('/outreach/preview', {
        template_id: selectedTemplate,
        phone: phone.trim(),
      })
      setPreview(response.data)
      setShowModal(true)
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error al generar la vista previa')
    } finally {
      setPreviewing(false)
    }
  }

  const handleSend = async () => {
    if (!preview) return
    setSending(true)
    setActionError(null)
    try {
      await apiClient.post('/outreach/send', {
        template_id: preview.template_id,
        phone: preview.phone,
      })
      setSuccessMsg(`Mensaje enviado exitosamente a ${preview.phone}`)
      setShowModal(false)
      setPreview(null)
      setPhone('')
      setSelectedTemplate('')
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Error al enviar el mensaje')
    } finally {
      setSending(false)
    }
  }

  if (!canUse) {
    return (
      <div>
          <EmptyState
          icon="🔒"
          title="Acceso denegado"
          description="Solo los Operadores y Administradores pueden acceder al módulo de Outreach."
        />
      </div>
    )
  }

  return (
    <div className="space-y-4">

      {successMsg && (
        <div className="rounded-card border border-brand-light bg-brand-light p-3 text-sm font-medium text-brand flex items-center justify-between">
          {successMsg}
          <button onClick={() => setSuccessMsg(null)} className="text-brand hover:underline text-xs">
            Cerrar
          </button>
        </div>
      )}
      {actionError && <ErrorAlert message={actionError} />}

      <div className="card max-w-lg p-6 space-y-5">
        <div className="space-y-1">
          <label htmlFor="template" className="label">Plantilla</label>
          {loadingTemplates ? (
            <LoadingSpinner size="sm" label="Cargando plantillas..." />
          ) : templatesError ? (
            <ErrorAlert message={templatesError} onRetry={fetchTemplates} />
          ) : (
            <select
              id="template"
              value={selectedTemplate}
              onChange={(e) => setSelectedTemplate(e.target.value)}
              className="input"
            >
              <option value="">— Selecciona una plantilla —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.title}</option>
              ))}
            </select>
          )}
        </div>

        <div className="space-y-1">
          <label htmlFor="phone" className="label">Número de teléfono</label>
          <input
            id="phone"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+51 999 999 999"
            className="input"
          />
          <p className="text-xs text-ink-faint">
            Solo números en la lista blanca pueden recibir mensajes en modo MANUAL_ALLOWLIST.
          </p>
        </div>

        <button
          onClick={() => void handlePreview()}
          disabled={previewing || !selectedTemplate || !phone.trim()}
          className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-50"
        >
          {previewing ? 'Generando vista previa…' : 'Vista previa'}
        </button>
      </div>

      {/* Preview modal */}
      {showModal && preview && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="card w-full max-w-md p-6 shadow-panel"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="section-title mb-4">Vista previa del mensaje</h3>
            <div className="mb-4 rounded-card bg-surface-subtle p-4">
              <p className="text-xs text-ink-muted">Para: <span className="mono">{preview.phone}</span></p>
              <p className="mt-2 text-sm text-ink whitespace-pre-wrap">{preview.preview_text}</p>
            </div>
            <p className="mb-5 text-sm text-ink-muted">¿Confirmas el envío de este mensaje?</p>
            <div className="flex gap-2">
              <button onClick={() => setShowModal(false)} className="btn-ghost flex-1">
                Cancelar
              </button>
              <button
                onClick={() => void handleSend()}
                disabled={sending}
                className="btn-primary flex-1 disabled:opacity-50"
              >
                {sending ? 'Enviando…' : 'Confirmar y enviar'}
              </button>
            </div>
            {actionError && <div className="mt-3"><ErrorAlert message={actionError} /></div>}
          </div>
        </div>
      )}
    </div>
  )
}
